import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';

const MODULE_LABELS = {
  expenses: 'Expenses',
  refunds: 'Refunds',
  maintenance: 'Truck Maintenance',
  compliance: 'Compliance',
  tasks: 'Tasks',
};

export default function NotificationConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // key like "expenses.submitted.5" while saving
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.getNotificationConfig();
      setConfig(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="admin-page"><div className="loading">Loading...</div></div>;
  if (error) return <div className="admin-page"><div className="alert alert-danger">{error}</div></div>;
  if (!config) return null;

  const { events, subscriptions, users } = config;

  // Only show users who have phone numbers
  const eligibleUsers = users.filter(u => u.phone && u.phone.trim() !== '');

  function isSubscribed(moduleKey, eventType, userId) {
    return subscriptions.some(s => s.module_key === moduleKey && s.event_type === eventType && s.user_id === userId);
  }

  function getThreshold(moduleKey, eventType) {
    const sub = subscriptions.find(s => s.module_key === moduleKey && s.event_type === eventType && s.threshold_days);
    return sub?.threshold_days || null;
  }

  async function toggleSubscription(moduleKey, eventType, userId, eventDef) {
    const key = `${moduleKey}.${eventType}.${userId}`;
    setSaving(key);
    try {
      if (isSubscribed(moduleKey, eventType, userId)) {
        await api.unsubscribeNotification({ module_key: moduleKey, event_type: eventType, user_id: userId });
      } else {
        const payload = { module_key: moduleKey, event_type: eventType, user_id: userId };
        if (eventDef.has_threshold) {
          const existing = getThreshold(moduleKey, eventType);
          payload.threshold_days = existing || eventDef.default_threshold;
        }
        await api.subscribeNotification(payload);
      }
      await load();
    } catch (err) {
      setError(err.message);
    }
    setSaving(null);
  }

  async function handleThresholdChange(moduleKey, eventType, value) {
    const days = parseInt(value);
    if (!days || days < 1) return;
    setSaving(`threshold.${moduleKey}.${eventType}`);
    try {
      await api.updateNotificationThreshold({ module_key: moduleKey, event_type: eventType, threshold_days: days });
      await load();
    } catch (err) {
      setError(err.message);
    }
    setSaving(null);
  }

  const moduleKeys = Object.keys(events);

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Notification Subscriptions</h1>
        <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '0.9rem' }}>
          Configure which users receive SMS alerts for each module event.
          Only users with phone numbers are shown.
        </p>
      </div>

      {eligibleUsers.length === 0 && (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          No users with phone numbers found. Add phone numbers to users first.
        </div>
      )}

      {moduleKeys.map(moduleKey => {
        const moduleEvents = events[moduleKey];
        return (
          <div key={moduleKey} className="card" style={{ marginBottom: '1.5rem', padding: 0, overflow: 'hidden' }}>
            <div style={{
              padding: '1rem 1.5rem',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#1e293b',
            }}>
              {MODULE_LABELS[moduleKey] || moduleKey}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <div className="table-wrapper">
              <table className="data-table" style={{ margin: 0, borderRadius: 0 }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: 200 }}>Event</th>
                    {eligibleUsers.map(u => (
                      <th key={u.id} style={{ textAlign: 'center', minWidth: 100, fontSize: '0.8rem' }}>
                        <div>{u.full_name}</div>
                        <div style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.7rem' }}>{u.role}</div>
                      </th>
                    ))}
                    <th style={{ minWidth: 80, textAlign: 'center' }}>Threshold</th>
                  </tr>
                </thead>
                <tbody>
                  {moduleEvents.map(evt => {
                    const threshold = getThreshold(moduleKey, evt.event);
                    return (
                      <tr key={evt.event}>
                        <td>
                          <span style={{ fontWeight: 500 }}>{evt.label}</span>
                          {evt.has_threshold && (
                            <span style={{ color: '#94a3b8', fontSize: '0.75rem', marginLeft: 8 }}>(daily alert)</span>
                          )}
                        </td>
                        {eligibleUsers.map(u => {
                          const subbed = isSubscribed(moduleKey, evt.event, u.id);
                          const key = `${moduleKey}.${evt.event}.${u.id}`;
                          const isSaving = saving === key;
                          return (
                            <td key={u.id} style={{ textAlign: 'center', padding: '0.5rem' }}>
                              <label style={{ cursor: isSaving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={subbed}
                                  disabled={isSaving}
                                  onChange={() => toggleSubscription(moduleKey, evt.event, u.id, evt)}
                                  style={{
                                    width: 18,
                                    height: 18,
                                    cursor: isSaving ? 'wait' : 'pointer',
                                    accentColor: '#d4a843',
                                  }}
                                />
                              </label>
                            </td>
                          );
                        })}
                        <td style={{ textAlign: 'center' }}>
                          {evt.has_threshold ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                              <input
                                type="number"
                                min="1"
                                max="30"
                                value={threshold || evt.default_threshold}
                                onChange={(e) => handleThresholdChange(moduleKey, evt.event, e.target.value)}
                                disabled={saving?.startsWith('threshold.')}
                                style={{
                                  width: 50,
                                  padding: '4px 6px',
                                  borderRadius: 4,
                                  border: '1px solid #d1d5db',
                                  textAlign: 'center',
                                  fontSize: '0.85rem',
                                }}
                              />
                              <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>days</span>
                            </div>
                          ) : (
                            <span style={{ color: '#d1d5db' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
